import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { CQAppConstant } from "../../../../app-constants";
import { ActionType, CQCombobox, IOption } from "../../../../common/cq-inputs";
import { CQMasterDataStore } from "../../../../store/cq-master-data.store";
import { TemplateSFObjectStore } from "../../../../store/models/template-object.model";
import { IFieldData } from "../../component-level-fields/component-field";
import LayoutFieldsConfigController from "../../layout-level-fields.controller";
import TemplateConfigurationController from "../../template-config.controller";

interface IProps {
        fieldData: IFieldData;
        templateName: string;
        action: {
        resetAndDisableSelectionPanel: (value: boolean) => void;
        generateComponentName: (value: string) => void;
    };
}

const templateConfigCtrl = new TemplateConfigurationController();
const layoutFieldsConfigCtrl = new LayoutFieldsConfigController();

const CQPicklist = (props: IProps) => {
    const {
        fieldData: { inputParamKey, defaultValue, value },
        templateName,
        action: { resetAndDisableSelectionPanel, generateComponentName },
    } = props;
    const outputConfigObject = CQMasterDataStore.OutputConfig.getStoreData(templateName);
    const [state, setState] = useState<string>(
        outputConfigObject[inputParamKey] || defaultValue
    );
    const objectName = outputConfigObject.selectedObject;

    let componentTypeValue = "";
    if (templateName === "cqEsig100") {
        componentTypeValue = outputConfigObject["componentType"];
    }

    let recordTypesValues: any[] = [];
    if (templateName === "cqForm100") {
        recordTypesValues =
            outputConfigObject.layout.recordTypes || [];
    }

    useEffect(() => {
        CQMasterDataStore.OutputConfig.updateTemplatePropertyValue(
            templateName,
            props.fieldData.inputParamKey,
            state
        );
        handleEsigAndComponentNameDisableForCqTemplate(state);
        generateComponentName(inputParamKey);

        if (
            inputParamKey === "objectApiName" &&
            (componentTypeValue === CQAppConstant.COMPONENT_TYPE_FORM ||
            componentTypeValue === CQAppConstant.COMPONENT_TYPE_LIGHTNING_COMPONENT)
        ) {
            templateConfigCtrl.getObjectFields(
                templateName,
                "objectApiNameField",
                state,
                false
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    function handleEsigAndComponentNameDisableForCqTemplate(value: string) {
        if (
            inputParamKey === CQAppConstant.COMPONENT_TYPE &&
            templateName === "cqEsig100"
        ) {
            let esigValue = outputConfigObject[CQAppConstant.ESIG];

            if (value === CQAppConstant.COMPONENT_TYPE_FLOW) {
                esigValue = false;
            }

            if (value === CQAppConstant.COMPONENT_TYPE_ESIG_ONLY) {
                esigValue = true;
            }

            if (value !== CQAppConstant.COMPONENT_TYPE_FORM) {
                CQMasterDataStore.OutputConfig.resetFromKey(templateName, [
                    "parentField",
                    "relatedField",
                    "selectedObject",
                    "selectedLayout",
                    "layout",
                    "ruleElements",
                    "inputRules",
                    "additionalFields",
                ]);

                templateConfigCtrl.resetLayoutList();
                CQMasterDataStore.RuleElements.reset();
                resetAndDisableSelectionPanel(true);
            } else {
                resetAndDisableSelectionPanel(false);
            }

            CQMasterDataStore.OutputConfig.updateTemplatePropertyValue(
                templateName,
                CQAppConstant.ESIG,
                esigValue
            );
        }
    }

    function getComboboxOptions(values: any[]) {
        const options: IOption[] = [];
        values.forEach((value: any, index: number) => {
            options.push({
                id: `${index + 1}`,
                label: value.label,
                value: value.value,
            });
        });

        return options;
    }

    function generateFieldsForSelectOptionsAsPerType(
        fields: any[],
        type: string,
        objectApiName?: string
    ) {
        const dropDowns: any[] = [];
        if (type.toLowerCase() === "reference") {
            fields.forEach((layoutField: any) => {
                if (
                    layoutField.fieldFullName !== "customSettings" &&
                    ((layoutField.fieldType &&
                    ["reference", "lookup", "masterdetail"].includes(
                        layoutField.fieldType.toLowerCase()
                    )) ||
                    (layoutField.fieldType &&
                        ["string", "textarea", "longtextarea", "text"].includes(
                        layoutField.fieldType.toLowerCase()
                        ) &&
                        layoutField.length === 18))
                ) {
                    dropDowns.push({
                    label: `${layoutField.fieldLabel} - ${layoutField.fieldFullName}`,
                    value: layoutField.fieldFullName,
                    });
                }
            });
        } else if (type === "referenceObject" && objectApiName!) {
            fields.forEach((layoutField: any) => {
                if (
                    layoutField.fieldFullName !== "customSettings" &&
                    Array.isArray(layoutField.referenceTo)
                        ? layoutField.referenceTo.includes(objectApiName)
                        : layoutField.referenceTo === objectApiName
                ) {
                    dropDowns.push({
                        label: `${layoutField.fieldLabel} - ${layoutField.fieldFullName}`,
                        value: layoutField.fieldFullName,
                    });
                }
            });
        } else if (type === "object") {
            if (fields.length > 0) {
                const indexToIgnore: number[] = [];
                TemplateSFObjectStore.sfObjects.forEach(
                    (object: any, index: number) => {
                        if (
                            object.label.includes(`( ${objectName} )`) &&
                            !indexToIgnore.includes(index)
                        ) {
                            indexToIgnore.push(index);
                            dropDowns.push({ label: object.label, value: object.name });
                        }
                    }
                );

                fields.forEach((layoutField: any) => {
                    if (
                        layoutField.fieldFullName !== "customSettings" &&
                        layoutField.fieldType &&
                        ["reference", "lookup", "masterdetail"].includes(
                            layoutField.fieldType.toLowerCase()
                        )
                    ) {
                        TemplateSFObjectStore.sfObjects.forEach(
                            (object: any, index: number) => {
                                if (
                                    object.label.includes(`( ${layoutField.referenceTo} )`) &&
                                    !indexToIgnore.includes(index)
                                ) {
                                    indexToIgnore.push(index);
                                    dropDowns.push({ label: object.label, value: object.name });
                                }
                            }
                        );
                    }
                });
            } else {
                TemplateSFObjectStore.sfObjects.forEach(
                    (object: any, index: number) => {
                        dropDowns.push({ label: object.label, value: object.name });
                    }
                );
            }
        } else {
            fields.forEach((layoutField: any) => {
                if (layoutField.fieldFullName !== "customSettings") {
                    dropDowns.push({
                        label: `${layoutField.fieldLabel} - ${layoutField.fieldFullName}`,
                        value: layoutField.fieldFullName,
                    });
                }
            });
        }

        return dropDowns;
    }

    const onValueChange = (values: IOption[]) => {
        setState(values.length === 0 ? "" : values[0].value);
    };

    //! TODO not a best solution need some improvement
    let values= value;
    if (inputParamKey === "relatedField") {
        const layoutFields = templateConfigCtrl.getFilterFields(
            outputConfigObject["objectApiNameField"].fields || []
        );

    if (
        componentTypeValue === CQAppConstant.COMPONENT_TYPE_LIGHTNING_COMPONENT
    ) {
        values = generateFieldsForSelectOptionsAsPerType(
            layoutFields,
            "reference"
        );
    } else {
        values = generateFieldsForSelectOptionsAsPerType(
            layoutFields,
            "referenceObject",
            objectName
        );
    }
    } else if (inputParamKey === "parentField") {
        const layoutFields = layoutFieldsConfigCtrl.getLayoutFields(templateName);
        values = generateFieldsForSelectOptionsAsPerType(
            layoutFields,
            "referenceObject",
            outputConfigObject["objectApiName"]
        );
    } else if (inputParamKey === "objectApiName") {
        if (
            outputConfigObject["componentType"] ===
                CQAppConstant.COMPONENT_TYPE_FORM
        ) {
            //Quick Action Object
            const layoutFields =
            layoutFieldsConfigCtrl.getLayoutFields(templateName);
            values = generateFieldsForSelectOptionsAsPerType(
                layoutFields,
                "object"
            );
        } else {
            values = generateFieldsForSelectOptionsAsPerType([], "object");
        }
    } else if (inputParamKey === "recordTypeDeveloperName") {
        values = recordTypesValues.map((record: any) => {
            const result = {
                label: record.Name,
                value: record.DeveloperName,
            };

            return result;
        });
    }

    const options = getComboboxOptions(values ? values : []);

    function onActionPerformed(actionType: ActionType, result: IOption[]) {
        if(actionType === 'item-cleared' || actionType === 'item-selected') {
            onValueChange(result)
        }
    }

    return (
        <CQCombobox
            key= {inputParamKey}
            config={{
              variation: "readonly",
            }}
            label={{
                label: "",
            }}
            onActionPerformed={(actionType, result) => onActionPerformed(actionType, result as IOption[])}
            options={options}
            limit={options.length}
            value={outputConfigObject[inputParamKey]}
        />
    );
};

export default observer(CQPicklist);
